import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Theme,
  Typography,
  useTheme,
  Grid,
  Paper,
  Button,
  Tabs,
  Tab,
  useMediaQuery,
} from '@mui/material';
import { Asset } from '@/types/asset_search';
import { useNavigate, useParams } from 'react-router';
import AssetDetails from './AssetDetails';
import { EmptyTableIcon } from '../Shared/Icons';
import { useGetVinSearchQuery } from '@/features/api/search.api';
import Spinner from '../Shared/Spinner';
import Mapbox from '../Mapbox/Mapbox';
import { useGetOverlayQuery } from '@/features/api/overlay.api';
import Can from '@/app/Auth/Can';
import { Action, Subject } from '@/types/enums';
import MapSplitPane from '../Shared/MapSplitPane';
import { useAppSelector } from '@/app/hooks';
import { ORG_MANHEIM_CODE } from '@/constants';

export default function AssetInfo() {
  const { t } = useTranslation();
  const siteMetadata = useAppSelector((state) => state.user.siteMetadata);
  const theme = useTheme<Theme>();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery('(min-width:900px)');
  const [sizes, setSizes] = useState([200, 200]);
  const [tabValue, setTabValue] = useState<number | boolean>(0);

  const assetUuid = useParams()?.assetUuid || '';

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  const siteId = useParams()?.siteId?.toLowerCase() || '';
  const tabItems = [{ name: t('assetDetail.details'), disabled: false }];
  const mapRef = useRef(null);
  const [selectedAsset, setSelectedAsset] = useState<Asset | undefined>(
    undefined,
  );

  const {
    data: overlaysData,
    isLoading: isLoadingOverlays,
    isFetching: isFetchingOverlays,
  } = useGetOverlayQuery(siteId, {
    skip: !siteId || !Can(Action.READ, Subject.geofence),
  });

  const {
    data: assetData,
    isLoading: isLoadingAsset,
    isFetching: isFetchingAsset,
  } = useGetVinSearchQuery(
    { siteId, params: assetUuid },
    { skip: !siteId || !assetUuid },
  );

  useEffect(() => {
    if (assetData?.length && assetData.length >= 1) {
      setSelectedAsset(assetData[0]);
    }
  }, [assetData]);

  useEffect(() => {
    if (siteMetadata.orgCode === ORG_MANHEIM_CODE) {
      navigate(`/${siteId}/assets?q=${assetUuid}`, { replace: true });
    }
  }, [siteMetadata.orgCode]);

  if (
    isLoadingAsset ||
    isFetchingAsset ||
    isLoadingOverlays ||
    isFetchingOverlays
  ) {
    return <Spinner />;
  } else if (!selectedAsset) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        height="auto"
        alignItems="center"
        justifyContent="center"
      >
        <EmptyTableIcon
          viewBox="0 0 64 64"
          style={{
            width: '64px',
            height: '64px',
          }}
          mode={theme.palette.mode}
        />
        <Typography
          component="h3"
          fontWeight="600"
          fontSize="16px"
          sx={(theme) => ({
            color: theme.palette.text.primary,
            margin: '12px 0',
          })}
        >
          {t('global.noResultsFound')}
        </Typography>
        <Box>
          <Button
            value="Back"
            data-testid="asset-back-button"
            sx={(theme) => ({
              display: 'flex',
              flexDirection: 'row',
              alignSelf: 'flex-end',
              paddingBottom: 0,
              paddingTop: 0,
              marginBottom: '10px',
              border: `2px solid ${theme.palette.text.primary}`,
              borderRadius: '20% / 50%',
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
            })}
            onClick={() => navigate(-1)}
          >
            ← {t('assetDetail.back')}
          </Button>
        </Box>
      </Box>
    );
  }
  return (
    <Box
      sx={{
        zIndex: 0,
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      <Grid
        container
        columns={{ sm: 6, md: 12 }}
        direction={{ xs: 'column-reverse', md: 'row' }}
        // sx={{ maxHeight: { xs: '100%', md: '100%' } }}
        sx={{
          maxHeight: { xs: '100%', md: '80vh' },
          height: isDesktop ? '800px' : '100%',
        }}
        data-testid="asset-info-grid"
      >
        <MapSplitPane
          setSizes={setSizes}
          isDesktop={isDesktop}
          panes={[
            <Grid
              key={0}
              size={{ xs: 6 }}
              sx={{
                maxWidth: { md: '100%!important' },
                flex: 'column',
                minHeight: { xs: '400px', md: '80vh' },
                width: '100%',
                maxHeight: { xs: '100%', md: '80vh' },
                overflow: 'auto',
              }}
            >
              <Paper
                sx={{
                  height: '800px',
                  display: 'flex',
                  flexDirection: 'column',
                  overflowY: 'auto',
                }}
              >
                <Box
                  sx={() => ({
                    paddingBottom: 0,
                    maxHeight: '25%',
                  })}
                >
                  <Box
                    sx={{
                      padding: '16px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '20px',
                    }}
                  >
                    <Box sx={{ width: { xs: '45%', sm: '55%', md: '55%' } }}>
                      <Typography
                        textAlign="left"
                        fontSize="16px"
                        fontWeight="600"
                      >
                        {`${
                          selectedAsset.vehicle_make
                            ? selectedAsset.vehicle_make
                            : t('assetDetail.vin')
                        }-${
                          selectedAsset.vehicle_model
                            ? selectedAsset.vehicle_model
                            : selectedAsset.asset_uuid
                        }`}
                      </Typography>
                    </Box>
                    <Box>
                      <Button
                        value="Back"
                        data-testid="asset-back-button"
                        sx={(theme) => ({
                          display: 'flex',
                          flexDirection: 'row',
                          alignSelf: 'flex-end',
                          paddingBottom: 0,
                          paddingTop: 0,
                          marginBottom: '10px',
                          border: `2px solid ${theme.palette.text.primary}`,
                          borderRadius: '20% / 50%',
                          borderColor: theme.palette.primary.main,
                          color: theme.palette.primary.main,
                        })}
                        onClick={() => navigate(-1)}
                      >
                        ← {t('assetDetail.back')}
                      </Button>
                    </Box>
                  </Box>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    centered
                    sx={{
                      width: '100%',
                      maxWidth: '100%',
                      zIndex: 1,
                      borderBottom: '1px solid rgba(188, 194, 204, 0.4)',
                    }}
                    TabIndicatorProps={{ style: { display: 'block' } }}
                  >
                    {tabItems.map((item: any) => (
                      <Tab
                        disableRipple
                        sx={{
                          width: '100%',
                          letterSpacing: { xs: '-.0001em', sm: '0' },
                          paddingLeft: { xs: '2px', sm: '5px' },
                          paddingRight: { xs: '2px', sm: '5px' },
                          fontSize: { xs: '10px', sm: '13px' },
                          maxWidth: `${100 / tabItems.length}%`,
                        }}
                        key={item.name}
                        label={
                          <Box
                            sx={{
                              textTransform: 'capitalize',
                              fontSize: '14px',
                              fontWeight: '600',
                            }}
                            data-testid={`${item.name}-tab`}
                          >
                            {item.name}
                          </Box>
                        }
                        disabled={item?.disabled}
                        data-testid={item.name}
                      />
                    ))}
                  </Tabs>
                </Box>
                <AssetDetails selectedAsset={selectedAsset} />
              </Paper>
            </Grid>,
            <Grid
              key={1}
              size={{ xs: 12 }}
              sx={{
                display: 'flex',
                flexFlow: 'column',
                minHeight: { xs: '400px', md: '80vh' },
                width: '100%',
                maxHeight: { xs: '100%!important', md: '80vh!important' },
                maxWidth: { md: '100%!important' },
                position: 'relative',
              }}
              data-testid="mapbox-container"
            >
              <Mapbox
                mapRef={mapRef}
                resized={sizes}
                data={[selectedAsset]}
                items={overlaysData?.items}
                selected={selectedAsset}
              />
            </Grid>,
          ]}
        />
      </Grid>
    </Box>
  );
}
